import { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useUserSearch } from "../hooks/use-broadcast";
import { UserSearchResult } from "../services/broadcast";

export interface PickedUser {
  id: string;
  full_name: string;
  phone: string | null;
}

interface Props {
  picked: PickedUser[];
  onChange: (next: PickedUser[]) => void;
}

function useDebouncedValue<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export function UserSearchSelect({ picked, onChange }: Props) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const debounced = useDebouncedValue(query, 300);
  const { data: results, isFetching } = useUserSearch(debounced);

  const pickedIds = new Set(picked.map((p) => p.id));

  const add = (u: UserSearchResult) => {
    if (!pickedIds.has(u.id)) {
      onChange([
        ...picked,
        { id: u.id, full_name: u.full_name, phone: u.phone },
      ]);
    }
    setQuery("");
  };
  const remove = (id: string) => onChange(picked.filter((p) => p.id !== id));

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">
        Specific users{" "}
        <span className="font-normal text-gray-400">
          (optional — hand-pick recipients)
        </span>
      </label>

      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          value={query}
          placeholder="Search by name, email or phone…"
          className="pl-8"
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
        />
        {open && debounced.trim().length >= 2 && (
          <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
            {isFetching && (
              <div className="px-3 py-2 text-sm text-gray-400">Searching…</div>
            )}
            {!isFetching && (results?.length ?? 0) === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400">No matches</div>
            )}
            {results?.map((u) => (
              <button
                key={u.id}
                type="button"
                disabled={pickedIds.has(u.id)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => add(u)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:opacity-40"
              >
                <span className="truncate">{u.full_name || "Unnamed"}</span>
                <span className="ml-3 shrink-0 text-xs text-gray-400">
                  {u.phone ?? ""}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {picked.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {picked.map((p) => (
            <span
              key={p.id}
              className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700"
            >
              {p.full_name || p.phone || p.id.slice(0, 8)}
              <button
                type="button"
                onClick={() => remove(p.id)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Remove"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
